import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import PrimaryButton from '../components/PrimaryButton';
import PlantCard from '../components/PlantCard';
import { plantsApi, zonesApi } from '../services/gardenApi';
import { useGardenPageTitle } from '../useGardenPageTitle';

function norm(s) {
  return (s || '').toString().trim().toLowerCase();
}

function plantMatchesZone(plant, zoneId, zoneName) {
  const ids = Array.isArray(plant.marker_zone_ids) ? plant.marker_zone_ids : [];
  if (ids.includes(zoneId)) return true;
  if (zoneName && norm(plant.location_notes) === norm(zoneName)) return true;
  return false;
}

export default function PlantsListScreen() {
  useGardenPageTitle('My plants', 'My plants');
  const navigate = useNavigate();
  const [plants, setPlants] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [zoneFilterId, setZoneFilterId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pr, zr] = await Promise.all([
        plantsApi.list(),
        zonesApi.list().catch(() => ({ zones: [] })),
      ]);
      setPlants(pr.plants || []);
      setZones(zr.zones || []);
    } catch (e) {
      setError(e.message || 'Failed to load plants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sortedZones = useMemo(
    () => [...zones].sort((a, b) => norm(a.name).localeCompare(norm(b.name))),
    [zones]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = plants;
    if (q) {
      list = list.filter((p) =>
        (p.common_name || '').toLowerCase().includes(q) ||
        (p.botanical_name || '').toLowerCase().includes(q) ||
        (p.nickname || '').toLowerCase().includes(q) ||
        (p.location_notes || '').toLowerCase().includes(q)
      );
    }
    if (zoneFilterId) {
      const z = zones.find((x) => x.id === zoneFilterId);
      list = list.filter((p) => plantMatchesZone(p, zoneFilterId, z?.name));
    }
    return list;
  }, [plants, query, zoneFilterId, zones]);

  return (
    <div className="relative">
      <header className="flex items-center justify-between px-4 pt-3 pb-2">
        <h1 className="text-lg font-semibold text-garden-ink">My plants</h1>
        <button
          onClick={() => navigate('/garden/plants/add')}
          className="min-h-11 min-w-11 -mr-2 rounded-full flex items-center justify-center text-garden-primary"
          aria-label="Add plant"
        >
          <Plus size={24} />
        </button>
      </header>

      {plants.length > 0 ? (
        <div className="px-4 pb-3 space-y-2">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-garden-muted"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your plants"
              className="w-full min-h-11 pl-9 pr-4 rounded-full bg-white border border-garden-ink/10 text-sm focus:outline-none focus:border-garden-primary"
            />
          </div>
          {sortedZones.length > 0 ? (
            <div>
              <label htmlFor="plants-zone-filter" className="sr-only">
                Filter by zone
              </label>
              <select
                id="plants-zone-filter"
                value={zoneFilterId}
                onChange={(e) => setZoneFilterId(e.target.value)}
                className="w-full min-h-11 px-4 rounded-full bg-white border border-garden-ink/10 text-sm text-garden-ink focus:outline-none focus:border-garden-primary appearance-none bg-[length:1rem] bg-[right_0.75rem_center] bg-no-repeat pr-10"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                }}
              >
                <option value="">All zones</option>
                {sortedZones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name || 'Unnamed zone'}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className="mx-4 mb-3 p-3 rounded-xl bg-red-50 text-sm text-red-700">{error}</div>
      ) : null}

      {loading ? (
        <div className="px-4 py-6 text-sm text-garden-muted">Loading…</div>
      ) : plants.length === 0 ? (
        <EmptyLibrary onAdd={() => navigate('/garden/plants/add')} />
      ) : filtered.length === 0 ? (
        <div className="px-4 py-6 text-sm text-garden-muted">
          {query.trim() && !zoneFilterId ? (
            <>No plants match &ldquo;{query.trim()}&rdquo;.</>
          ) : (
            'No plants match your filters.'
          )}
        </div>
      ) : (
        <div className="px-4 pb-24 space-y-2">
          {filtered.map((p) => (
            <PlantCard
              key={p.id}
              plant={p}
              onClick={() => navigate(`/garden/plants/${p.id}`)}
            />
          ))}
        </div>
      )}

      {plants.length > 0 ? (
        <div className="fixed bottom-20 right-4 z-20">
          <button
            onClick={() => navigate('/garden/plants/add')}
            className="w-14 h-14 rounded-full bg-garden-primary text-white shadow-lg flex items-center justify-center active:scale-95 transition"
            aria-label="Add plant"
          >
            <Plus size={28} />
          </button>
        </div>
      ) : null}
    </div>
  );
}

function EmptyLibrary({ onAdd }) {
  return (
    <div className="px-6 py-10 flex flex-col items-center text-center">
      <div className="text-6xl mb-3" aria-hidden>🌿</div>
      <h2 className="text-lg font-semibold text-garden-ink">No plants yet</h2>
      <p className="mt-2 text-sm text-garden-muted max-w-xs">
        Add the plants you grow — roses, tomatoes, your favorite hydrangea.
        You can edit details any time.
      </p>
      <div className="mt-5">
        <PrimaryButton onClick={onAdd}>
          <Plus size={18} /> Add your first plant
        </PrimaryButton>
      </div>
    </div>
  );
}
